import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, X, Loader2, MessageCircle } from 'lucide-react';

// ElevenLabs Agent ID - Created in ElevenLabs Dashboard
const AGENT_ID = 'agent_3001knjrf2hrec48fzf871ty0chv';

export const VoiceAgent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [widgetLoaded, setWidgetLoaded] = useState(false);

  useEffect(() => {
    // Load ElevenLabs widget script
    if (!document.getElementById('elevenlabs-widget-script')) {
      const script = document.createElement('script');
      script.id = 'elevenlabs-widget-script';
      script.src = 'https://elevenlabs.io/convai-widget/index.js';
      script.async = true;
      script.onload = () => setWidgetLoaded(true);
      document.body.appendChild(script);
    } else {
      setWidgetLoaded(true);
    }
  }, []);

  const handleOpen = () => {
    setIsOpen(true);
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1500);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Floating Call Button */}
      <motion.button
        onClick={handleOpen}
        className="fixed bottom-24 right-6 z-40 w-16 h-16 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F4D06F] shadow-lg flex items-center justify-center group"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="voice-agent-button"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: 'spring' }}
      >
        <Phone size={28} className="text-[#0A0A0A]" />
        
        {/* Pulse animation */}
        <span className="absolute inset-0 rounded-full bg-[#D4AF37] animate-ping opacity-30" />
        
        {/* Tooltip */}
        <span className="absolute right-full mr-3 px-3 py-2 bg-[#121212] text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Talk to AI Assistant
        </span>
      </motion.button>

      {/* Voice Agent Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={handleClose}
            />
            
            {/* Modal Content */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-md glass rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#F4D06F]/10 p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <Phone size={24} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Kirit Sobti AI Assistant</h3>
                      <p className="text-sm text-white/50">
                        {isLoading ? 'Connecting...' : 'Ready to help • Available 24/7'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-white/60 hover:text-white p-2"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* Agent Content */}
              <div className="p-6">
                {isLoading ? (
                  <div className="text-center py-8">
                    <Loader2 size={48} className="text-[#D4AF37] mx-auto mb-4 animate-spin" />
                    <p className="text-white/70">Connecting to AI Assistant...</p>
                  </div>
                ) : (
                  <>
                    {/* ElevenLabs Widget */}
                    <div className="mb-6 flex justify-center min-h-[100px]">
                      {widgetLoaded && (
                        <elevenlabs-convai agent-id={AGENT_ID}></elevenlabs-convai>
                      )}
                    </div>

                    {/* Info Cards */}
                    <div className="space-y-3 mt-4">
                      <p className="text-sm text-white/50 text-center mb-4">
                        Ask me about:
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          'Discovery Call Booking',
                          'Advisory Packages',
                          'Africa Business Entry',
                          'Investment Questions'
                        ].map((topic) => (
                          <div
                            key={topic}
                            className="px-3 py-2 bg-white/5 rounded-lg text-xs text-white/70 text-center"
                          >
                            {topic}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* WhatsApp Alternative */}
                    <div className="mt-6 pt-4 border-t border-white/10 text-center">
                      <p className="text-xs text-white/40 mb-3">Or reach us directly:</p>
                      <a
                        href="https://wa.me/919899969196"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366]/20 text-[#25D366] rounded-full text-sm hover:bg-[#25D366]/30 transition-colors"
                      >
                        <MessageCircle size={16} />
                        WhatsApp +91 98999 69196
                      </a>
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-white/10 bg-white/5">
                <p className="text-xs text-white/40 text-center">
                  Powered by ElevenLabs AI • Available 24/7
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
